/**
 * ProseMirror schema for `.zwdoc` documents (#248).
 *
 * Node and mark names, attrs and attr order must match
 * `schemas/zwdoc.schema.json`: `Node.toJSON()` output for this schema *is* the
 * on-disk block format. Spec order is significant — ProseMirror emits marks in
 * schema order, so reordering marks changes every file that uses them.
 *
 * `toDOM` / `parseDOM` (#249) are plain data + attribute reads on the element
 * ProseMirror hands in: the editor renders with them, paste from Word / Google
 * Docs parses with them, and export (#258) serializes with them.
 */

import {
  Schema,
  type AttributeSpec,
  type Attrs,
  type DOMOutputSpec,
  type TagParseRule,
} from "prosemirror-model";
import type { ZwDocAlign } from "./generated/zwDoc";

export const ZWDOC_ALIGNS: readonly ZwDocAlign[] = [
  "left",
  "center",
  "right",
  "justify",
];

export const ZWDOC_MAX_INDENT = 6;

const SPACER_HEIGHT_RE = /^[0-9]*\.?[0-9]+(em|px|rem)$/;

function stringAttr(defaultValue: string): AttributeSpec {
  return { default: defaultValue, validate: "string" };
}

// --- DOM helpers ------------------------------------------------------------

/** Inline style + data attrs for a textblock's alignment and indent. */
function blockDomAttrs(attrs: Attrs): Record<string, string> {
  const align = String(attrs.align);
  const indent = Number(attrs.indent);
  const style: string[] = [];
  const out: Record<string, string> = {};
  if (align !== "left") style.push(`text-align: ${align}`);
  if (indent > 0) {
    style.push(`margin-left: ${indent * 2}em`);
    out["data-indent"] = String(indent);
  }
  if (style.length > 0) out.style = style.join("; ");
  return out;
}

function parseAlign(dom: HTMLElement): ZwDocAlign {
  const fromStyle = dom.style.textAlign;
  const value = (
    fromStyle !== "" ? fromStyle : (dom.getAttribute("align") ?? "")
  ).toLowerCase();
  return ZWDOC_ALIGNS.find((align) => align === value) ?? "left";
}

function parseIndent(dom: HTMLElement): number {
  const value = Number.parseInt(dom.getAttribute("data-indent") ?? "", 10);
  if (Number.isInteger(value) === false) return 0;
  return Math.min(Math.max(value, 0), ZWDOC_MAX_INDENT);
}

function blockParseAttrs(dom: HTMLElement): Attrs {
  return { align: parseAlign(dom), indent: parseIndent(dom) };
}

function headingRule(level: number): TagParseRule {
  return {
    tag: `h${level}`,
    getAttrs: (dom: HTMLElement) => ({ level, ...blockParseAttrs(dom) }),
  };
}

const alignAttr: AttributeSpec = {
  default: "left",
  validate: (value: unknown) => {
    if (ZWDOC_ALIGNS.includes(value as ZwDocAlign) === false) {
      throw new RangeError(`Invalid align: ${String(value)}`);
    }
  },
};

const indentAttr: AttributeSpec = {
  default: 0,
  validate: (value: unknown) => {
    if (
      typeof value !== "number" ||
      Number.isInteger(value) === false ||
      value < 0 ||
      value > ZWDOC_MAX_INDENT
    ) {
      throw new RangeError(`Invalid indent: ${String(value)}`);
    }
  },
};

function integerAttr(
  defaultValue: number,
  min: number,
  max: number,
): AttributeSpec {
  return {
    default: defaultValue,
    validate: (value: unknown) => {
      if (
        typeof value !== "number" ||
        Number.isInteger(value) === false ||
        value < min ||
        value > max
      ) {
        throw new RangeError(`Invalid integer attr: ${String(value)}`);
      }
    },
  };
}

export const zwdocSchema = new Schema({
  nodes: {
    doc: { content: "block+" },
    paragraph: {
      group: "block",
      content: "inline*",
      attrs: { align: alignAttr, indent: indentAttr },
      parseDOM: [{ tag: "p", getAttrs: blockParseAttrs }],
      toDOM: (node): DOMOutputSpec => ["p", blockDomAttrs(node.attrs), 0],
    },
    heading: {
      group: "block",
      content: "inline*",
      defining: true,
      attrs: {
        level: integerAttr(1, 1, 6),
        align: alignAttr,
        indent: indentAttr,
      },
      parseDOM: [1, 2, 3, 4, 5, 6].map(headingRule),
      toDOM: (node): DOMOutputSpec => [
        `h${String(node.attrs.level)}`,
        blockDomAttrs(node.attrs),
        0,
      ],
    },
    blockquote: {
      group: "block",
      content: "block+",
      defining: true,
      parseDOM: [{ tag: "blockquote" }],
      toDOM: (): DOMOutputSpec => ["blockquote", 0],
    },
    bullet_list: {
      group: "block",
      content: "list_item+",
      parseDOM: [{ tag: "ul" }],
      toDOM: (): DOMOutputSpec => ["ul", 0],
    },
    ordered_list: {
      group: "block",
      content: "list_item+",
      attrs: { start: integerAttr(1, 0, Number.MAX_SAFE_INTEGER) },
      parseDOM: [
        {
          tag: "ol",
          getAttrs: (dom: HTMLElement) => {
            const start = Number.parseInt(dom.getAttribute("start") ?? "1", 10);
            return { start: Number.isInteger(start) && start >= 0 ? start : 1 };
          },
        },
      ],
      toDOM: (node): DOMOutputSpec =>
        node.attrs.start === 1
          ? ["ol", 0]
          : ["ol", { start: String(node.attrs.start) }, 0],
    },
    list_item: {
      content: "block+",
      defining: true,
      parseDOM: [{ tag: "li" }],
      toDOM: (): DOMOutputSpec => ["li", 0],
    },
    code_block: {
      group: "block",
      content: "text*",
      marks: "",
      code: true,
      defining: true,
      attrs: { language: stringAttr("") },
      parseDOM: [
        {
          tag: "pre",
          preserveWhitespace: "full",
          getAttrs: (dom: HTMLElement) => ({
            language: dom.getAttribute("data-language") ?? "",
          }),
        },
      ],
      toDOM: (node): DOMOutputSpec => [
        "pre",
        node.attrs.language === ""
          ? {}
          : { "data-language": String(node.attrs.language) },
        ["code", 0],
      ],
    },
    horizontal_rule: {
      group: "block",
      parseDOM: [{ tag: "hr" }],
      toDOM: (): DOMOutputSpec => ["hr"],
    },
    spacer: {
      group: "block",
      atom: true,
      attrs: {
        height: {
          default: "2em",
          validate: (value: unknown) => {
            if (
              typeof value !== "string" ||
              SPACER_HEIGHT_RE.test(value) === false
            ) {
              throw new RangeError(`Invalid spacer height: ${String(value)}`);
            }
          },
        },
      },
      parseDOM: [
        {
          tag: "div.zw-spacer",
          getAttrs: (dom: HTMLElement) => {
            const height = dom.getAttribute("data-height") ?? "";
            return SPACER_HEIGHT_RE.test(height) ? { height } : false;
          },
        },
      ],
      toDOM: (node): DOMOutputSpec => [
        "div",
        {
          class: "zw-spacer",
          "data-height": String(node.attrs.height),
          style: `height: ${String(node.attrs.height)}`,
          "aria-hidden": "true",
        },
      ],
    },
    raw: {
      group: "block",
      atom: true,
      attrs: { markdown: stringAttr("") },
      parseDOM: [
        {
          tag: "div.zw-raw",
          getAttrs: (dom: HTMLElement) => ({
            markdown: dom.getAttribute("data-markdown") ?? "",
          }),
        },
      ],
      toDOM: (node): DOMOutputSpec => [
        "div",
        { class: "zw-raw", "data-markdown": String(node.attrs.markdown) },
        ["pre", String(node.attrs.markdown)],
      ],
    },
    text: { group: "inline" },
    hard_break: {
      group: "inline",
      inline: true,
      selectable: false,
      parseDOM: [{ tag: "br" }],
      toDOM: (): DOMOutputSpec => ["br"],
    },
    image: {
      group: "inline",
      inline: true,
      atom: true,
      draggable: true,
      attrs: {
        src: stringAttr(""),
        alt: stringAttr(""),
        title: stringAttr(""),
      },
      parseDOM: [
        {
          tag: "img[src]",
          getAttrs: (dom: HTMLElement) => ({
            src: dom.getAttribute("src") ?? "",
            alt: dom.getAttribute("alt") ?? "",
            title: dom.getAttribute("title") ?? "",
          }),
        },
      ],
      toDOM: (node): DOMOutputSpec => [
        "img",
        {
          src: String(node.attrs.src),
          alt: String(node.attrs.alt),
          ...(node.attrs.title === ""
            ? {}
            : { title: String(node.attrs.title) }),
        },
      ],
    },
    comment_anchor: {
      group: "inline",
      inline: true,
      atom: true,
      selectable: false,
      attrs: { id: stringAttr("") },
      parseDOM: [
        {
          tag: "span.zw-comment-anchor",
          getAttrs: (dom: HTMLElement) => ({
            id: dom.getAttribute("data-comment-id") ?? "",
          }),
        },
      ],
      toDOM: (node): DOMOutputSpec => [
        "span",
        {
          class: "zw-comment-anchor",
          "data-comment-id": String(node.attrs.id),
        },
      ],
    },
  },
  marks: {
    link: {
      inclusive: false,
      attrs: { href: stringAttr(""), title: stringAttr("") },
      parseDOM: [
        {
          tag: "a[href]",
          getAttrs: (dom: HTMLElement) => ({
            href: dom.getAttribute("href") ?? "",
            title: dom.getAttribute("title") ?? "",
          }),
        },
      ],
      toDOM: (mark): DOMOutputSpec => [
        "a",
        {
          href: String(mark.attrs.href),
          ...(mark.attrs.title === ""
            ? {}
            : { title: String(mark.attrs.title) }),
        },
        0,
      ],
    },
    // `excludes: ""` lets overlapping comment threads share text.
    comment: {
      inclusive: false,
      excludes: "",
      attrs: { id: stringAttr("") },
      parseDOM: [
        {
          tag: "span.zw-comment",
          getAttrs: (dom: HTMLElement) => ({
            id: dom.getAttribute("data-comment-id") ?? "",
          }),
        },
      ],
      toDOM: (mark): DOMOutputSpec => [
        "span",
        { class: "zw-comment", "data-comment-id": String(mark.attrs.id) },
        0,
      ],
    },
    bold: {
      parseDOM: [
        { tag: "strong" },
        // Google Docs wraps pasted content in <b style="font-weight:normal">.
        {
          tag: "b",
          getAttrs: (dom: HTMLElement) =>
            dom.style.fontWeight === "normal" ? false : null,
        },
        {
          style: "font-weight",
          getAttrs: (value: string) =>
            /^(bold(er)?|[6-9]\d{2})$/.test(value) ? null : false,
        },
      ],
      toDOM: (): DOMOutputSpec => ["strong", 0],
    },
    italic: {
      parseDOM: [{ tag: "em" }, { tag: "i" }, { style: "font-style=italic" }],
      toDOM: (): DOMOutputSpec => ["em", 0],
    },
    underline: {
      parseDOM: [{ tag: "u" }, { style: "text-decoration=underline" }],
      toDOM: (): DOMOutputSpec => ["u", 0],
    },
    strike: {
      parseDOM: [
        { tag: "s" },
        { tag: "del" },
        { tag: "strike" },
        { style: "text-decoration=line-through" },
      ],
      toDOM: (): DOMOutputSpec => ["s", 0],
    },
    highlight: {
      parseDOM: [{ tag: "mark" }],
      toDOM: (): DOMOutputSpec => ["mark", 0],
    },
    code: {
      parseDOM: [{ tag: "code" }],
      toDOM: (): DOMOutputSpec => ["code", 0],
    },
  },
});
